import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { customReportAPI } from '../../utils/api';
import type { SavedReport, ExportJobStatus, ExportReportRequest } from '../../types/customReport';
import TimezoneSelect from '../TimezoneSelect';
import { useAuth } from '../../hooks/useAuth';
import { getTodayDateInNurseryTimezone } from '../../utils/timezone';

interface Props {
  show: boolean;
  onHide: () => void;
  report: SavedReport;
}

const ExportModal: React.FC<Props> = ({ show, onHide, report }) => {
  const { t } = useTranslation(['pages', 'common']);
  const { currentNursery } = useAuth();
  const [timezone, setTimezone] = useState(currentNursery?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [format, setFormat] = useState<'csv' | 'pdf'>('csv');
  const [dateOverride, setDateOverride] = useState({ from: '', to: '', is_today: false });
  const [useSavedFilters, setUseSavedFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ExportJobStatus | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  /** Stable id for poll URL — status responses may omit job_id; replacing state would drop it and yield GET .../exports/undefined */
  const exportPollJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (show) {
      setLoading(false);
      setError(null);
      setJobStatus(null);
      setRetryCount(0);
      exportPollJobIdRef.current = null;
      setUseSavedFilters(true);
      setDateOverride({ from: '', to: '', is_today: false });
      setFormat('csv');
      setTimezone(currentNursery?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    }
  }, [show, currentNursery?.timezone]);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    setJobStatus(null);
    exportPollJobIdRef.current = null;
    setRetryCount(0);
    try {
      if (!currentNursery?.nursery_id) {
        setError(t('errors.selectNursery', { ns: 'common' }));
        setLoading(false);
        return;
      }

      const payload: ExportReportRequest = {
        report_id: report.id,
        format: format,
        timezone
      };

      if (useSavedFilters && report.filters && typeof report.filters === 'object') {
        // Send filters in the JSON body so Laravel parses nested values as arrays.
        // Export previously loaded only DB JSON; json_decode without associative arrays
        // yields stdClass and can trigger "Cannot use object of type stdClass as array".
        payload.filters = JSON.parse(JSON.stringify(report.filters));
      }

      if (!useSavedFilters) {
        payload.filter_overrides = {
          is_today: dateOverride.is_today
        };
        if (!dateOverride.is_today && dateOverride.from && dateOverride.to) {
          payload.filter_overrides.date_from = dateOverride.from;
          payload.filter_overrides.date_to = dateOverride.to;
        }
      }

      const response = await customReportAPI.export(currentNursery?.nursery_id ?? '', payload);

      if (response instanceof Blob) {
        const dateStamp = getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC');
        downloadBlob(response, `${report.name}_${dateStamp}.${format}`);
        onHide();
      } else {
        const jobId =
          (response as { job_id?: string }).job_id ??
          (response as { data?: { job_id?: string } }).data?.job_id;
        if (jobId != null && jobId !== '') {
          const idStr = String(jobId);
          exportPollJobIdRef.current = idStr;
          setJobStatus({
            success: true,
            job_id: idStr,
            status: 'queued'
          });
          setLoading(false);
        } else {
          setError(t('customReports.errors.exportFailed'));
          setLoading(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('customReports.errors.exportFailed'));
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval: any;
    const MAX_RETRIES = 3;

    if (jobStatus && (jobStatus.status === 'processing' || jobStatus.status === 'queued')) {
      interval = setInterval(async () => {
        try {
          const pollId = exportPollJobIdRef.current;
          if (!pollId || !currentNursery?.nursery_id) return;

          const status = await customReportAPI.checkExportJob(currentNursery.nursery_id, pollId);
          setJobStatus((prev) => ({
            ...status,
            job_id:
              status.job_id != null && String(status.job_id) !== ''
                ? String(status.job_id)
                : prev?.job_id ?? pollId
          }));
          setRetryCount(0);
          
          if (status.status === 'completed' && status.download_url) {
            exportPollJobIdRef.current = null;
            const link = document.createElement('a');
            link.href = status.download_url;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            onHide();
          } else if (status.status === 'failed') {
            setError(status.error || status.message || t('customReports.errors.exportFailed'));
            exportPollJobIdRef.current = null;
            setJobStatus(null);
            setLoading(false);
          } else if (status.status === 'expired') {
            setError(t('customReports.errors.exportExpired'));
            exportPollJobIdRef.current = null;
            setJobStatus(null);
            setLoading(false);
          }
        } catch (err: any) {
          console.error('Failed to check export job status', err);
          const nextRetry = retryCount + 1;
          setRetryCount(nextRetry);

          if (nextRetry >= MAX_RETRIES || err.response?.status === 404 || err.response?.status === 403) {
            setError(t('customReports.errors.checkExportFailed'));
            exportPollJobIdRef.current = null;
            setJobStatus(null);
            setLoading(false);
            if (interval) clearInterval(interval);
          }
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [jobStatus, currentNursery, onHide, t, retryCount]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('customReports.exportModal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        {jobStatus ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5>
              {jobStatus.status === 'queued' 
                ? t('customReports.exportModal.queued') 
                : t('customReports.exportModal.processing')}
            </h5>
            {jobStatus.progress !== undefined && jobStatus.status === 'processing' && (
              <ProgressBar now={jobStatus.progress} label={`${jobStatus.progress}%`} className="mt-3" />
            )}
          </div>
        ) : (
          <>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">{t('customReports.format')}</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  id="format-csv"
                  label={t('customReports.exportCSV')}
                  name="exportFormat"
                  checked={format === 'csv'}
                  onChange={() => setFormat('csv')}
                />
                <Form.Check
                  type="radio"
                  id="format-pdf"
                  label={t('customReports.exportPDF')}
                  name="exportFormat"
                  checked={format === 'pdf'}
                  onChange={() => setFormat('pdf')}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('customReports.exportModal.timezone')}</Form.Label>
              <TimezoneSelect value={timezone} onChange={(val) => setTimezone(val || 'UTC')} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label={t('customReports.exportModal.useSavedFilters')}
                checked={useSavedFilters}
                onChange={(e) => setUseSavedFilters(e.target.checked)}
              />
            </Form.Group>

            {!useSavedFilters && (
              <div className="border p-3 rounded mb-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="small fw-bold mb-0">{t('customReports.exportModal.dateOverride')}</Form.Label>
                  <Form.Check
                    type="switch"
                    id="export-today-switch"
                    label={t('customReports.today')}
                    checked={dateOverride.is_today}
                    onChange={(e) => setDateOverride({ ...dateOverride, is_today: e.target.checked })}
                    className="small"
                  />
                </div>
                {!dateOverride.is_today ? (
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateOverride.from}
                      onChange={(e) => setDateOverride({ ...dateOverride, from: e.target.value })}
                    />
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateOverride.to}
                      onChange={(e) => setDateOverride({ ...dateOverride, to: e.target.value })}
                    />
                  </div>
                ) : (
                  <div className="small text-muted italic">
                    {t('customReports.todayFilterActive')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {t('buttons.cancel', { ns: 'common' })}
        </Button>
        {!jobStatus && (
          <Button variant="primary" onClick={handleExport} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : t('customReports.export')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
